/**
 * Hugo design tokens — single source of truth for the redesign.
 *
 * Aesthetic target: Granola (restrained palette + airy whitespace) + Jack and
 * Jill (warm B2C-premium). Wispr Flow / V7 contribute texture only.
 *
 * Theme name: "Quiet Confidence" — see .design/hugo-hero-redesign/THEME_SPEC.md.
 *
 * Mode: light is the default everywhere. The logged-in app also has a dark
 * scheme the user picks in Settings → Appearance. Colour tokens are CSS
 * custom properties (`var(--a4-ink, #16132A)`): the light value is the
 * fallback and the :root value, the dark value applies under
 * html[data-theme="dark"], which only the logged-in app sets. Raw hex for
 * code that needs a real colour (the MUI palette) lives in `schemes`.
 *
 * Type:
 *   - Plus Jakarta Sans   — body, UI, all headings by default
 *   - Instrument Serif    — hero accent (1–2 words italic only)
 *   - JetBrains Mono      — code / numeric tabular
 *   Banned by frontend-design: Inter, Roboto, Arial. Do not reintroduce.
 *
 * These are framework-agnostic raw values. The MUI theme in design/theme.next.js
 * builds on top of them. Refactor *new* components to consume tokens from here;
 * existing components keep working off the legacy theme.js until migrated.
 */

// ----------------------------------------------------------------------------
// COLOUR SCHEMES
// ----------------------------------------------------------------------------
// Every colour token is registered here with its light and dark value and
// handed out as a CSS variable. Light values are the shipped design and must
// not change without a design review; dark values are the logged-in app's
// dark mode (deep ink-violet ground, near-white text, brightened violet).

const schemes = { light: {}, dark: {} };

const cssVar = (name, light, dark = light) => {
  schemes.light[name] = light;
  schemes.dark[name] = dark;
  return `var(--${name}, ${light})`;
};

const ramp = (prefix, pairs) =>
  Object.fromEntries(Object.entries(pairs).map(([k, [light, dark]]) => [k, cssVar(`${prefix}-${k}`, light, dark)]));

// ----------------------------------------------------------------------------
// PALETTE
// ----------------------------------------------------------------------------
// One brand accent (Hugo purple, preserved). Everything else is neutral.
// Granola lesson: lots of near-white + near-black; gradients only where the
// eye should land (primary CTA, hero accent).
//
// In dark mode the ramps flip: low steps become deep tints, high steps become
// light text colours, so `brand[700]` text on a `brand[50]` tint reads in both.

const palette = {
  // Brand — keep recognizable but stop using it for every surface
  brand: ramp('brand', {
    50: ['#F5F1FF', '#1E1733'],
    100: ['#E9DFFE', '#2A2147'],
    200: ['#D2BFFD', '#3A2E63'],
    300: ['#B69CFB', '#5A49A0'],
    400: ['#9A78F9', '#7B66D6'],
    500: ['#6C5CE7', '#9B82FF'], // primary — the existing Hugo purple
    600: ['#7B47E0', '#A993FF'],
    700: ['#5C38A8', '#BFAEFF'],
    800: ['#3F2575', '#D6CCFF'],
    900: ['#241441', '#ECE6FF'],
  }),

  // Brand as a button fill under white text. Same as `brand` in light; stays
  // a saturated violet in dark so white labels keep AA contrast.
  brandSolid: ramp('brand-solid', {
    500: ['#6C5CE7', '#7A52F5'],
    600: ['#7B47E0', '#7048F2'],
    700: ['#5C38A8', '#6236E8'],
  }),

  // Neutrals — warm grey, slight purple bias so brand reads as part of the family
  neutral: ramp('neutral', {
    0:   ['#FFFFFF', '#171320'],
    25:  ['#FCFBFE', '#0F0C17'], // page bg, light
    50:  ['#F7F6FA', '#14111D'],
    100: ['#EFEDF3', '#211C2C'],
    200: ['#E2DFE8', '#2A2436'],
    300: ['#C9C5D2', '#3A3348'],
    400: ['#9F9AAB', '#6F6A82'],
    500: ['#74707F', '#9B96AD'],
    600: ['#534F5C', '#C4BFD3'],
    700: ['#37343F', '#DAD6E4'],
    800: ['#1F1D26', '#E9E6F0'], // page bg, dark
    900: ['#121017', '#F3F1F8'],
    950: ['#08070C', '#FBFAFE'],
  }),

  // Semantic accents — used sparingly
  success: ramp('success', { 100: ['#DCFBE8', '#12301F'], 500: ['#22C55E', '#34D399'], 700: ['#15803D', '#6EE7A0'] }),
  warning: ramp('warning', { 100: ['#FEF3C7', '#33270F'], 500: ['#F59E0B', '#FBBF24'], 700: ['#B45309', '#F5B651'] }),
  danger:  ramp('danger',  { 100: ['#FEE2E2', '#3A1618'], 500: ['#EF4444', '#F87171'], 700: ['#B91C1C', '#FF8A8A'] }),
  info:    ramp('info',    { 100: ['#DBEAFE', '#13233F'], 500: ['#3B82F6', '#60A5FA'], 700: ['#1D4ED8', '#8AB4FF'] }),
};

// ----------------------------------------------------------------------------
// SEMANTIC COLOR ROLES
// ----------------------------------------------------------------------------
// Components should reference these roles, not raw palette swatches.
// `colorRoles.light` is the live role map (CSS variables, so it follows the
// app's scheme despite the name); `colorRoles.dark` is the raw dark values.

const roles = {
  bg:            ['#FCFBFE', '#0F0C17'],
  bgSubtle:      ['#F7F6FA', '#14111D'],
  surface:       ['#FFFFFF', '#171320'],
  surfaceMuted:  ['#EFEDF3', '#211C2C'],
  border:        ['#E2DFE8', '#2A2436'],
  borderStrong:  ['#C9C5D2', '#3A3348'],
  textPrimary:   ['#121017', '#F3F1F8'],
  textSecondary: ['#534F5C', '#C4BFD3'],
  // neutral[500] = 4.81:1 on surface, 4.66:1 on bg (WCAG AA for body text).
  textTertiary:  ['#74707F', '#9B96AD'],
  textOnBrand:   ['#FFFFFF', '#FFFFFF'],
  brand:         ['#6C5CE7', '#9B82FF'],
  brandHover:    ['#7B47E0', '#A993FF'],
  brandSubtle:   ['#F5F1FF', '#1E1733'],
  focusRing:     ['rgba(108, 92, 231, 0.45)', 'rgba(165, 139, 255, 0.55)'],
};

const colorRoles = {
  light: Object.fromEntries(Object.entries(roles).map(([k, [light, dark]]) => [k, cssVar(`role-${k}`, light, dark)])),
  dark: Object.fromEntries(Object.entries(roles).map(([k, [, dark]]) => [k, dark])),
};

// ----------------------------------------------------------------------------
// TYPOGRAPHY
// ----------------------------------------------------------------------------
// Modular scale 1.25 (major third). Plus Jakarta Sans for body + all headings
// (warm geometric grotesque). Instrument Serif reserved for hero accent words
// only — 1–2 words italic, never body. Inter/Roboto/Arial are banned by the
// frontend-design skill; do not reintroduce.

const fontFamily = {
  // Default sans for body + all UI + all headings.
  sans: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", sans-serif',
  // Display alias — same face as sans by design. Lets components opt into a
  // "display" semantic without forcing a face swap.
  display: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", sans-serif',
  // Editorial serif. Apply via inline override on the 1–2 hero accent words
  // only (e.g., `sx={{ fontFamily: theme.tokens.fontFamily.serif, fontStyle: 'italic' }}`).
  serif: '"Instrument Serif", "Cormorant Garamond", Georgia, "Times New Roman", serif',
  mono: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
};

// Sizes (px). Use these named tokens, not the numbers.
const fontSize = {
  xs:   12,
  sm:   14,
  base: 16,
  lg:   18,
  xl:   20,
  '2xl': 24,
  '3xl': 30,
  '4xl': 38,
  '5xl': 48,
  '6xl': 60,
  '7xl': 76,
};

const lineHeight = {
  tight:   1.1,   // display
  snug:    1.25,  // headings
  normal:  1.5,   // body
  relaxed: 1.7,   // long-form
};

const fontWeight = {
  regular:   400,
  medium:    500,
  semibold:  600,
  bold:      700,
  extrabold: 800, // hero headline only
};

const letterSpacing = {
  tight:   '-0.02em',
  display: '-0.03em', // hero headline (tighter than other headings)
  normal:  '0em',
  wide:    '0.02em',
};

// ----------------------------------------------------------------------------
// SPACING (4pt grid)
// ----------------------------------------------------------------------------
const space = {
  0:   0,
  1:   4,
  2:   8,
  3:   12,
  4:   16,
  5:   20,
  6:   24,
  8:   32,
  10:  40,
  12:  48,
  16:  64,
  20:  80,
  24:  96,
  32:  128,
  40:  160,
  48:  192,
  64:  256,
};

// ----------------------------------------------------------------------------
// RADIUS
// ----------------------------------------------------------------------------
const radius = {
  none: 0,
  xs:   4,
  sm:   8,
  md:   12,
  lg:   16,
  xl:   24,
  '2xl': 32,
  pill: 999,
};

// ----------------------------------------------------------------------------
// ELEVATION (shadow)
// ----------------------------------------------------------------------------
// Soft, layered shadows. The `glass` ones add a subtle inner highlight for
// Wispr Flow-style glassmorphism cards on dark backgrounds.

const elevation = {
  none: 'none',
  xs:   '0 1px 2px rgba(15, 12, 26, 0.06)',
  sm:   '0 2px 6px rgba(15, 12, 26, 0.08)',
  md:   '0 6px 16px rgba(15, 12, 26, 0.10)',
  lg:   '0 16px 40px rgba(15, 12, 26, 0.14)',
  xl:   '0 32px 80px rgba(15, 12, 26, 0.20)',
  glassLight: '0 8px 32px rgba(15, 12, 26, 0.06), inset 0 1px 0 rgba(255,255,255,0.6)',
  glassDark:  '0 8px 32px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255,255,255,0.06)',
  brandGlow:  '0 12px 32px rgba(108, 92, 231, 0.35)',
};

// ----------------------------------------------------------------------------
// MOTION
// ----------------------------------------------------------------------------
// Restrained. NO decorative float/pulse/shimmer/glow loops. Reserve motion
// for state changes (hover, focus, entrance). The one exception is waiting:
// loops live only in shared/components/ui/Loading.js, and stop with
// reduced motion.

const duration = {
  instant: '0ms',
  fast:    '120ms',
  base:    '180ms',
  slow:    '280ms',
  entrance: '420ms',
};

const easing = {
  standard: 'cubic-bezier(0.2, 0, 0, 1)',   // most UI transitions
  emphasized: 'cubic-bezier(0.3, 0, 0, 1.15)', // entrance / hero
  exit:     'cubic-bezier(0.4, 0, 1, 1)',
};

// ----------------------------------------------------------------------------
// BREAKPOINTS (kept aligned to MUI defaults so legacy code coexists)
// ----------------------------------------------------------------------------
const breakpoints = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
};

// ----------------------------------------------------------------------------
// Z-INDEX (single scale to prevent the bidding war)
// ----------------------------------------------------------------------------
const zIndex = {
  base:    0,
  raised:  10,
  dropdown: 1000,
  sticky:  1100,
  overlay: 1200,
  modal:   1300,
  popover: 1400,
  toast:   1500,
  tooltip: 1600,
};

// ----------------------------------------------------------------------------
// LAYOUT
// ----------------------------------------------------------------------------
const layout = {
  containerMax:  1200,   // typical section width
  contentMax:    720,    // long-form reading width
  navHeight:     72,
};

// ----------------------------------------------------------------------------
// EDITORIAL (marketing homepage, "A4" direction approved 2026-10-25)
// ----------------------------------------------------------------------------
// The homepage runs on its own brighter violet and an editorial type scale.
// Values are lifted from the approved mockup. `muted` is darkened from the
// mockup's #8E8AA3 so small grey text passes WCAG AA on white and on bg.

const editorial = {
  color: {
    bg:         '#FBFAFE',
    ink:        '#16132A',
    ink2:       '#55516B',
    muted:      '#6E6987',
    marquee:    '#77728F',
    marqueeDot: '#D9D2F0',
    line:       '#ECE8F5',
    card:       '#FFFFFF',
    violet:     '#6C3FF0',
    violet2:    '#8A5CF6',
    violetInk:  '#4A22C8',
    violetTint: '#F2EDFF',
    violetSoft: '#C9B5FF',
    violetQuote: '#CDBBFF',
    lav:        '#F1ECFF',
    lavInk:     '#2E1A7A',
    mist:       '#F4F2F8',
    tough:      '#1D1534',
    deepInk:    '#24164F',
    surfaceSoft: '#FBFAFE',
    avatar:     '#EEEAF6',
    track:      '#F0EDF7',
    segBg:      '#F3F0FA',
    optLine:    '#DCD6EA',
    chipBg:     '#FCFBFE',
    rec:        '#E5487A',
    recBg:      '#FFE4EC',
    recDot:     '#FF4D6A',
    ok:         '#7EE2A8',
    pink:       '#F472B6',
    peach:      '#FFB48A',
  },
  gradient: {
    ctaViolet:   'linear-gradient(180deg,#8257F5,#6533E6)',
    bubble:      'linear-gradient(135deg,#7A4DF4,#5B2DDD)',
    tileViolet:  'linear-gradient(160deg,#7C52F4,#5A2CDC)',
    qzone:       'linear-gradient(180deg,#F8F5FF,#fff)',
    panel1:      'linear-gradient(160deg,#F1ECFF,#F8F5FE)',
    panel2:      'linear-gradient(160deg,#F7F1FB,#EFEAFE)',
    panel3:      'linear-gradient(160deg,#EEEBFD,#F7F5FE)',
    heroGlow:    'radial-gradient(40% 40% at 20% 45%,rgba(196,170,255,.32),transparent 70%),radial-gradient(34% 36% at 82% 30%,rgba(255,196,160,.35),transparent 70%)',
    stageGlow:   'radial-gradient(50% 55% at 50% 50%,rgba(108,63,240,.55),rgba(244,114,182,.25) 55%,transparent 75%)',
    band:        'radial-gradient(60% 80% at 15% 20%,rgba(160,120,255,.45),transparent 60%),radial-gradient(50% 70% at 90% 90%,rgba(255,180,200,.18),transparent 60%),linear-gradient(160deg,#6F42F2,#4A1FC4)',
    closeGlow:   'radial-gradient(closest-side,rgba(196,170,255,.35),transparent)',
  },
  shadow: {
    ctaViolet:      'inset 0 1px 0 rgba(255,255,255,.25),0 14px 34px -10px rgba(108,63,240,.65)',
    ctaVioletHover: 'inset 0 1px 0 rgba(255,255,255,.25),0 20px 44px -10px rgba(108,63,240,.75)',
    demo:           '0 0 0 1px rgba(22,19,42,.07),0 50px 100px -40px rgba(60,30,150,.45)',
    bubble:         '0 18px 40px -16px rgba(91,45,221,.6)',
    card:           '0 0 0 1px rgba(22,19,42,.05),0 24px 50px -26px rgba(60,30,150,.35)',
    tileHover:      '0 26px 50px -24px rgba(40,20,100,.45)',
    tilePlay:       '0 6px 16px -6px rgba(0,0,0,.35)',
    tilePlayViolet: '0 6px 16px -6px rgba(108,63,240,.6)',
    planBest:       '0 30px 60px -30px rgba(108,63,240,.45)',
    close:          '0 40px 80px -50px rgba(60,30,150,.35)',
    seg:            '0 1px 3px rgba(40,20,100,.12)',
  },
  radius: {
    xs: 6, sm: 7, md: 12, lg: 14, xl: 16, card: 18, bubble: 18, tile: 22,
    plan: 24, demo: 28, panel: 28, close: 32,
  },
  // Font sizes in px, straight from the mockup.
  fontSize: {
    micro: 9.5, tag: 10.5, chip: 11.5, label: 12.5, meta: 13, small: 13.5,
    ui: 14, nav: 14.5, body: 15, bodyLg: 15.5, lead: 17, lede: 18, sub: 19,
    bubble: 22, aside: 23, tile: 25, mq: 27, tileSerif: 31, stepH: 40,
    price: 56, big: 68, stepN: 120,
    h1: 'clamp(46px,6.5vw,94px)',
    h2: 'clamp(36px,5vw,64px)',
    band: 'clamp(44px,7.2vw,104px)',
    q: 'clamp(22px,2.2vw,28px)',
  },
  letterSpacing: {
    h1: '-.055em', h2: '-.05em', h3: '-.045em', tile: '-.035em', q: '-.03em',
    tight: '-.02em', serif: '-.01em', mono: '.06em',
  },
  space: { gutter: 32, gutterSm: 16, section: 150, sectionSm: 100, wrap: 1200, stage: 1060 },
  easing: 'cubic-bezier(.2,.8,.2,1)',
  noise: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
  // Dot field palette (RGB triplets) for the hero canvas.
  dots: [[108, 63, 240], [176, 140, 250], [236, 150, 200], [138, 92, 246]],
};

// ----------------------------------------------------------------------------
// A4 — the marketing design language (approved October 2026)
// ----------------------------------------------------------------------------
// Near-white lavender ground, one strong colour (Hugo violet), soft lavender
// tints used sparingly. Heavy Plus Jakarta Sans headlines carry ONE italic
// Instrument Serif accent word in violet. Logged-out pages (home, auth, blog,
// guides, FAQ, firms) consume these; the app keeps the roles above.

const a4 = {
  color: {
    bg:         cssVar('a4-bg', '#FBFAFE', '#0F0C17'), // page ground
    card:       cssVar('a4-card', '#FFFFFF', '#171320'),
    ink:        cssVar('a4-ink', '#16132A', '#F3F1F8'), // headlines, primary text, dark buttons
    ink2:       cssVar('a4-ink2', '#55516B', '#C4BFD3'), // body copy on bg (7.3:1)
    muted:      cssVar('a4-muted', '#6E6A85', '#9B96AD'), // small print, meta (4.97:1 on bg — AA)
    faint:      cssVar('a4-faint', '#8E8AA3', '#6F6A82'), // decoration only, never text
    line:       cssVar('a4-line', '#ECE8F5', '#2A2436'), // hairlines, card rings
    lineStrong: cssVar('a4-line-strong', '#DCD6EA', '#3A3348'), // input borders, ghost buttons
    violet:     cssVar('a4-violet', '#6C3FF0', '#A58BFF'), // the one strong colour
    violet2:    cssVar('a4-violet2', '#8A5CF6', '#B9A3FF'),
    violetInk:  cssVar('a4-violet-ink', '#4A22C8', '#C9B8FF'), // violet text on tints (7.8:1 on violetTint)
    violetTint: cssVar('a4-violet-tint', '#F2EDFF', '#231B3D'), // chips, quiet callouts
    tough:      cssVar('a4-tough', '#1D1534', '#241C3A'), // the one dark surface
    // Panel washes — lavender, never more than one per view
    wash:       cssVar('a4-wash', 'linear-gradient(160deg, #F1ECFF, #F8F5FE)', 'linear-gradient(160deg, #1E1833, #171320)'),
    washAlt:    cssVar('a4-wash-alt', 'linear-gradient(160deg, #EEEBFD, #F7F5FE)', 'linear-gradient(160deg, #1C1730, #16121F)'),
    violetFill: cssVar('a4-violet-fill', 'linear-gradient(180deg, #8257F5, #6533E6)'),
    danger:     cssVar('a4-danger', '#B42318', '#FF8A80'),
    // Logged-in app additions
    bg2:        cssVar('a4-bg2', '#F6F3FC', '#211C2C'), // firm monograms, quiet tags
    track:      cssVar('a4-track', '#F0EDF7', '#262031'), // score bar track
    seg:        cssVar('a4-seg', '#F1EDF9', '#1D1828'), // segmented control ground
    warn:       cssVar('a4-warn', '#B4532A', '#F2A57E'), // "closing soon"
    warnTint:   cssVar('a4-warn-tint', '#FFF1E8', '#33201A'),
    ok:         cssVar('a4-ok', '#1F7A55', '#5FD39A'), // a step done
    deepInk:    cssVar('a4-deep-ink', '#24164F', '#E4DCFF'), // quotes and role-fit text on lavender
    // Text on an `ink` or `violet` fill. White in light; in dark those fills
    // turn light, so the label turns to the ground colour.
    onInk:      cssVar('a4-on-ink', '#FFFFFF', '#0F0C17'),
    onViolet:   cssVar('a4-on-violet', '#FFFFFF', '#0F0C17'),
    lineHover:  cssVar('a4-line-hover', '#CFC6E4', '#4A4260'), // ghost button hover ring
    segOn:      cssVar('a4-seg-on', '#FFFFFF', '#2E2740'), // the picked option in a segmented control
    hoverTint:  cssVar('a4-hover-tint', 'rgba(108,63,240,.05)', 'rgba(165,139,255,.08)'),
    // Frosted bars: the desktop sidebar, the phone tab bar, the phone top bar.
    glassNav:   cssVar('a4-glass-nav', 'rgba(255,255,255,.55)', 'rgba(20,17,29,.72)'),
    glassBar:   cssVar('a4-glass-bar', 'rgba(255,255,255,.86)', 'rgba(23,19,32,.88)'),
    glassTop:   cssVar('a4-glass-top', 'rgba(251,250,254,.82)', 'rgba(15,12,23,.82)'),
    tintCard:   cssVar('a4-tint-card', 'linear-gradient(160deg,#F4EFFF,#FAF8FF)', 'linear-gradient(160deg,#1E1833,#191527)'),
    tintRing:   cssVar('a4-tint-ring', 'rgba(108,63,240,.10)', 'rgba(165,139,255,.20)'),
    scrim:      cssVar('a4-scrim', 'rgba(15,12,26,0.45)', 'rgba(5,4,10,0.7)'),
    // Soft violet and peach light at the top of every app page.
    glow:       cssVar(
      'a4-glow',
      'radial-gradient(38% 60% at 30% 0%,rgba(196,170,255,.22),transparent 70%),radial-gradient(30% 50% at 92% 0%,rgba(255,196,160,.18),transparent 70%)',
      'radial-gradient(38% 60% at 30% 0%,rgba(124,92,250,.16),transparent 70%),radial-gradient(30% 50% at 92% 0%,rgba(255,150,120,.06),transparent 70%)',
    ),
    // Logos and other images drawn for a white page sit on this in dark mode.
    logoBacking: cssVar('a4-logo-backing', 'transparent', '#F3F1F8'),
  },
  // Type scale (px). Display sizes are fluid clamps; letter-spacing tightens
  // as size grows. Body is 17px — the A4 reading size.
  type: {
    display: { size: 'clamp(46px, 6.5vw, 94px)', tracking: '-0.055em', leading: 0.98 },
    page:    { size: 'clamp(40px, 5.4vw, 72px)', tracking: '-0.05em', leading: 1.0 },
    h2:      { size: 'clamp(32px, 4.2vw, 56px)', tracking: '-0.05em', leading: 1.04 },
    h3:      { size: 'clamp(24px, 2.4vw, 32px)', tracking: '-0.04em', leading: 1.12 },
    h4:      { size: '20px', tracking: '-0.025em', leading: 1.3 },
    lede:    { size: '19px', leading: 1.5 },
    body:    { size: '17px', leading: 1.55 },
    prose:   { size: '18px', leading: 1.72 }, // long-form reading
    small:   { size: '14.5px', leading: 1.5 },
    label:   { size: '13px', leading: 1.4 },
    accentScale: '1.06em', // serif accent word sits slightly larger
  },
  measure: '68ch',
  radius: {
    sm: 10,
    md: 16,
    card: 24,
    panel: 28,
    pill: 999,
    // Logged-in app: inputs and monograms, tiles inside grids, cards.
    control: 12,
    tile: 16,
    appCard: 20,
    appCardSm: 18,
  },
  // Spacing scale (px). Nothing off it in layout; 14/18/22 only inside
  // type-driven pieces like list rows and score bars.
  space: [0, 4, 8, 12, 16, 20, 24, 28, 32, 40, 48, 64, 96, 150],
  // Logged-in page frame and rhythm, from the approved app mockups.
  app: {
    page: 1120, // default page column
    narrow: 860, // single-column pages (settings, job detail, browse)
    reading: 760, // feedback and other long reads
    gutter: { xs: 16, md: 48 },
    top: { xs: 20, md: 36 },
    bottom: { xs: 110, md: 80 }, // clears the phone tab bar
    headGap: { xs: 20, md: 28 }, // page head to first content
    gap: 16, // between cards in a grid
    section: { xs: 28, md: 36 }, // between hairline sections
    hit: 44, // minimum touch target
  },
  // In dark mode shadows give way to hairline rings and a faint violet glow.
  shadow: {
    ring:  cssVar('a4-shadow-ring', '0 0 0 1px #ECE8F5', '0 0 0 1px #2A2436'),
    card:  cssVar('a4-shadow-card', '0 0 0 1px rgba(22,19,42,.05), 0 24px 50px -26px rgba(60,30,150,.35)', '0 0 0 1px #2A2436, 0 24px 50px -26px rgba(0,0,0,.6)'),
    lift:  cssVar('a4-shadow-lift', '0 0 0 1px rgba(22,19,42,.07), 0 50px 100px -40px rgba(60,30,150,.45)', '0 0 0 1px #332C42, 0 50px 100px -40px rgba(0,0,0,.7)'),
    nav:   cssVar('a4-shadow-nav', '0 0 0 1px rgba(22,19,42,.06), 0 12px 40px -12px rgba(76,40,180,.22)', '0 0 0 1px #2A2436, 0 12px 40px -12px rgba(0,0,0,.6)'),
    violet: cssVar('a4-shadow-violet', 'inset 0 1px 0 rgba(255,255,255,.25), 0 14px 34px -10px rgba(108,63,240,.55)', 'inset 0 1px 0 rgba(255,255,255,.2), 0 14px 34px -12px rgba(124,92,250,.45)'),
    best:  cssVar('a4-shadow-best', '0 0 0 2px #6C3FF0, 0 30px 60px -30px rgba(108,63,240,.45)', '0 0 0 2px #A58BFF, 0 30px 60px -30px rgba(124,92,250,.35)'),
    // App cards: a hairline ring plus a soft violet drop.
    appCard: cssVar('a4-shadow-app-card', '0 0 0 1px rgba(22,19,42,.06), 0 18px 40px -28px rgba(60,30,150,.28)', '0 0 0 1px #2A2436, 0 18px 40px -28px rgba(0,0,0,.55)'),
    menu:  cssVar('a4-shadow-menu', '0 0 0 1px rgba(22,19,42,.06), 0 18px 40px -20px rgba(60,30,150,.35)', '0 0 0 1px #332C42, 0 18px 40px -20px rgba(0,0,0,.7)'),
    bar:   cssVar('a4-shadow-bar', '0 0 0 1px rgba(22,19,42,.07), 0 14px 34px -14px rgba(60,30,150,.35)', '0 0 0 1px #2A2436, 0 14px 34px -14px rgba(0,0,0,.7)'),
    navItem: cssVar('a4-shadow-nav-item', '0 6px 14px -10px rgba(60,30,150,.35)', '0 0 0 0 transparent'),
    seg:   cssVar('a4-shadow-seg', '0 1px 3px rgba(40,20,100,.14)', '0 0 0 1px #3A3348'),
    popover: cssVar('a4-shadow-popover', '0 1px 2px rgba(15,12,26,0.04), 0 24px 64px -16px rgba(15,12,26,0.18)', '0 0 0 1px #332C42, 0 24px 64px -16px rgba(0,0,0,.7)'),
  },
  glass: {
    background: cssVar('a4-glass', 'rgba(255,255,255,.72)', 'rgba(23,19,32,.78)'),
    blur: 'blur(18px) saturate(1.6)',
  },
  ease: 'cubic-bezier(.2,.8,.2,1)',
  reveal: { duration: 800, offset: 24 },
  nav: { height: 60, heightMobile: 56, top: 14, max: 760 },
  container: 1200,
  gutter: { xs: 16, md: 32 },
};

// CSS for the variables above: light on :root, dark under the attribute the
// logged-in app sets on <html>. Injected once by GlobalStyles.
const toDecls = (map) => Object.entries(map).map(([k, v]) => `--${k}:${v};`).join('');
// [data-theme="light"] inside a dark page puts its subtree back to light.
const cssVariables = () =>
  `:root{${toDecls(schemes.light)}}` +
  `html[data-theme="dark"]{color-scheme:dark;${toDecls(schemes.dark)}}` +
  `html[data-theme="dark"] [data-theme="light"]{color-scheme:light;${toDecls(schemes.light)}}`;

module.exports = {
  schemes,
  cssVariables,
  editorial,
  a4,
  palette,
  colorRoles,
  fontFamily,
  fontSize,
  lineHeight,
  fontWeight,
  letterSpacing,
  space,
  radius,
  elevation,
  duration,
  easing,
  breakpoints,
  zIndex,
  layout,
};
